 class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  async open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);

    // Ensure BYOS sub_item_products and bundle data are available in drawer
    try {
      await ensureByosDataInDrawer();
    } catch (e) {
      console.warn('[CartDrawer] ensureByosDataInDrawer failed', e);
    }

    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

// Ensure BYOS data exists in cart drawer by hydrating from /cart.js and /products/<handle>.js
async function ensureByosDataInDrawer() {
  if (window.__ensuringByos) return window.__ensuringByos;
  window.__ensuringByos = (async () => {
    try {
      const cart = await fetch('/cart.js', { cache: 'no-cache' }).then(r => r.json());
      const items = Array.isArray(cart?.items) ? cart.items : [];
      const byosItems = items.filter(i =>
        i?.properties?.['Bundle Type'] === 'Build Your Own Set Item' &&
        i?.properties?._bundle_item === 'true'
      );
      if (byosItems.length === 0) {
        return { setItems: [] };
      }

      const map = new Map();
      byosItems.forEach(i => {
        const pid = String(i.product_id);
        if (!map.has(pid)) {
          map.set(pid, { id: i.product_id, title: i.product_title || i.title, handle: i.handle, subItemProducts: [] });
        }
      });

      const entries = Array.from(map.values());
      for (const entry of entries) {
        let handle = entry.handle;
        if (!handle) {
          const found = byosItems.find(i => String(i.product_id) === String(entry.id) && i.handle);
          if (found?.handle) handle = found.handle;
        }
        if (!handle) continue;
        try {
          const p = await fetch(`/products/${handle}.js`, { cache: 'no-cache' }).then(r => r.json());
          const custom = (p.metafields || p.metafields_global || {}).custom || {};
          const raw = custom.sub_item_products || p.metafields_global?.custom?.sub_item_products;
          const value = raw?.value ?? custom.sub_item_products?.value ?? p.metafields_global?.custom?.sub_item_products?.value;
          if (value) {
            const list = (Array.isArray(value) ? value : [value]).map(o => {
              const variant = o.selected_or_first_available_variant || (o.variants?.[0] || null);
              const sku = (variant?.sku || o.metafields?.custom?.sku_product || o.sku) || undefined;
              return {
                id: o.id?.toString(),
                title: o.title,
                handle: o.handle,
                sku,
                variants: o.variants && Array.isArray(o.variants) ? o.variants : (variant ? [variant] : []),
              };
            });
            entry.subItemProducts = list;
          }
        } catch (err) {
          console.warn('[DrawerHydrate] Failed to fetch product JSON for', handle, err);
        }
      }

      window.buildYourOwnSubItems = { setItems: entries };

      try {
        // Minimal cart-derived BYOS bundles
        const buildByosBundlesFromCart = (cartData) => {
          const sets = {};
          (cartData.items || []).forEach(item => {
            const props = item.properties || {};
            const setId = props['Bundle Set ID'];
            const isMain = props['Bundle Type'] === 'Build Your Own Set' && props._bundle_main === 'true';
            const isItem = props['Bundle Type'] === 'Build Your Own Set Item' && props._bundle_item === 'true';
            if (!setId || (!isMain && !isItem)) return;
            if (!sets[setId]) sets[setId] = { mainProduct: null, subProducts: [], bundleSetId: setId };
            if (isMain) {
              sets[setId].mainProduct = {
                id: item.product_id,
                title: item.title,
                handle: item.handle,
                price: item.price,
                sku: item.sku,
                quantity: item.quantity,
              };
            } else if (isItem) {
              const matched = entries.find(e => String(e.id) === String(item.product_id));
              sets[setId].subProducts.push({
                id: item.id,
                variant_id: item.variant_id,
                productId: item.product_id,
                title: item.title,
                sku: item.sku || (item.properties && item.properties['sku_product']) || '',
                quantity: item.quantity,
                price: item.price,
                handle: item.handle,
                image: item.image_url || '',
                url: item.url,
                available: true,
                subItemProducts: matched?.subItemProducts || [],
              });
            }
          });
          return Object.values(sets)
            .filter(set => set.mainProduct && set.subProducts.length > 0)
            .map(set => ({ ...set, type: 'build-your-own-set' }));
        };

        window.cartBundleData = buildByosBundlesFromCart(cart);
        window.cartData = cart;
      } catch (err) {
        console.warn('[DrawerHydrate] Could not build cartBundleData', err);
      }

      return window.buildYourOwnSubItems;
    } catch (e) {
      console.warn('[DrawerHydrate] ensureByosDataInDrawer failed', e);
      return { setItems: [] };
    }
  })();
  return window.__ensuringByos;
}

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
